import { clsx, type ClassValue } from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
export const SANDBOX_LOCAL_STORAGE = 'CONFIG_SANDBOX';
export const sandboxCode = (game: string): string => {
  const sandboxes = localStorage.getItem(SANDBOX_LOCAL_STORAGE);
  if (!sandboxes) {
    return import.meta.env.VITE_ADMIN_SANDBOX_CODE;
  }
  const sandboxesObject = JSON.parse(sandboxes) as {
    game: string;
    sandbox: string;
  }[];
  const currentGameSandbox = sandboxesObject.find(
    (sandbox) => sandbox.game === game,
  );
  if (!currentGameSandbox) {
    return import.meta.env.VITE_ADMIN_SANDBOX_CODE;
  }
  return currentGameSandbox.sandbox;
};

/**
 * Store the game and sandbox code in the local storage
 * @param game
 * @param sandbox
 */
export const storeGameSandbox = (game: string, sandbox: string) => {
  const sandboxes = localStorage.getItem(SANDBOX_LOCAL_STORAGE);
  if (sandboxes) {
    const itemObject = JSON.parse(sandboxes) as {
      game: string;
      sandbox: string;
    }[];
    const item = itemObject.find((item) => item.game === game);
    if (item) {
      item.sandbox = sandbox;
    } else {
      itemObject.push({ game, sandbox });
    }
    localStorage.setItem(SANDBOX_LOCAL_STORAGE, JSON.stringify(itemObject));
  } else {
    localStorage.setItem(
      SANDBOX_LOCAL_STORAGE,
      JSON.stringify([{ game, sandbox }]),
    );
  }
};

export function getUTCOffset(
  timezone: string,
  date: Date = new Date(),
): string {
  // 'xxx' gives "+01:00", 'xx' gives "+0100", 'x' gives "+01"
  const offset = formatInTimeZone(date, timezone, 'xxx'); // "+01:00"

  const hours = offset.slice(0, 3); // "+01"
  const minutes = offset.slice(4, 6); // "00"

  // Only show minutes if non-zero (e.g., India is UTC+05:30)
  if (minutes === '00') {
    return `UTC${hours}`; // "UTC+01"
  }
  return `UTC${hours}:${minutes}`; // "UTC+05:30"
}
/** Calendar date + optional wall-clock time for ordering comparisons (never date-only when times are enabled). */
export const composeComparableDateTime = (
  calendarDate: Date,
  timeStr: string | undefined,
  includeTime: boolean,
): Date => {
  const d = new Date(calendarDate);
  if (includeTime) {
    const raw = timeStr ?? '00:00';
    const [h = 0, m = 0] = raw.split(':').map(Number);
    d.setHours(h, m, 0, 0);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return d;
};
